import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { PersonRelationType } from '@dealstack/shared/lib/generated';
import { editPersonRelation } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/apis/useRelatedPeople.ts';
import { EditRelatedPersonFormValues } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EditRelatedPersonDrawer/utils/types.ts';
import { RelatedPersonFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EditRelatedPersonDrawer/utils/relatedPersonShape.ts';
import { PersonDataContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/usePersonData.ts';

export const useEditRelatedPersonDrawer = (
	onClose: () => void,
	originalRelationType?: PersonRelationType
) => {
	const { selectedEntityId, selectedEntityName } = useContext(CapManageControllerContext);
	const { refetchRelatedPeople } = useContext(PersonDataContext);

	const editPersonRelationMutation = useMutation({
		mutationFn: editPersonRelation,
	});

	const editPersonRelationForm = useForm<EditRelatedPersonFormValues>({
		mode: 'all',
		resolver: zodResolver(RelatedPersonFormShape),
	});

	const onFormSubmit = async (formValues: EditRelatedPersonFormValues) => {
		const { relation_type, relation_id, create_legal_rep, custom_relation_type } = formValues;

		const isPromotingToPiv =
			relation_type === PersonRelationType.PIV &&
			originalRelationType !== PersonRelationType.PIV;
		const createLegalRepresentative = isPromotingToPiv && create_legal_rep;

		return editPersonRelationMutation.mutateAsync(
			{
				personId: selectedEntityId,
				relationId: relation_id,
				body: {
					type: relation_type,
					...(relation_type === PersonRelationType.OTHER
						? { customRelationType: custom_relation_type?.trim() }
						: {}),
					...(createLegalRepresentative ? { createLegalRepresentative: true } : {}),
				},
			},
			{
				onSuccess: () => {
					void refetchRelatedPeople();
					onClose();
				},
			}
		);
	};

	return {
		editPersonRelationForm,
		onFormSubmit,
		isLoading: editPersonRelationMutation.isPending,
		selectedEntityName,
	};
};
